import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { API_CONFIG } from '../../../core/config/api.config';

@Component({
  selector: 'app-trial-banner',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    @if (visible && countdown) {
      <div class="trial-banner" [class.urgent]="totalDays <= 3" [class.expired]="totalDays <= 0">
        <div class="banner-content">
          <i class="pi pi-clock"></i>
          @if (totalDays > 0) {
            <span>{{ 'TRIAL_BANNER.MESSAGE_COUNTDOWN' | translate }} </span>
            <span class="countdown">{{ countdown }}</span>
          } @else {
            <span>{{ 'TRIAL_BANNER.MESSAGE_EXPIRED' | translate }}</span>
          }
          <a class="upgrade-link" (click)="goToBilling()">
            {{ 'TRIAL_BANNER.UPGRADE' | translate }}
            <i class="pi pi-arrow-right"></i>
          </a>
        </div>
      </div>
    }
  `,
  styles: [`
    .trial-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: linear-gradient(135deg, #6366f1, #8b5cf6);
      color: white;
      padding: 0.5rem 1.25rem;
      font-size: 0.8125rem;
      font-weight: 500;
      z-index: 1000;
    }
    .trial-banner.urgent {
      background: linear-gradient(135deg, #f59e0b, #d97706);
    }
    .trial-banner.expired {
      background: linear-gradient(135deg, #ef4444, #dc2626);
    }
    .banner-content {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex: 1;
    }
    .banner-content i { font-size: 0.875rem; }
    .countdown {
      font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
      font-weight: 700;
      font-size: 0.875rem;
      letter-spacing: 0.5px;
      background: rgba(255,255,255,0.2);
      padding: 2px 8px;
      border-radius: 4px;
    }
    .upgrade-link {
      color: white;
      font-weight: 700;
      cursor: pointer;
      text-decoration: underline;
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      margin-left: 0.5rem;
    }
    .upgrade-link:hover { text-decoration: none; }
  `]
})
export class TrialBannerComponent implements OnInit, OnDestroy {
  visible = false;
  countdown: string | null = null;
  totalDays = 0;

  private trialEndDate: Date | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  ngOnInit() {
    const companyId = localStorage.getItem('selectedCompanyId');
    if (!companyId) return;

    // Try cached data first (set during company selection)
    const cachedStatus = localStorage.getItem('selectedCompanySubscriptionStatus');
    const cachedTrialEnd = localStorage.getItem('selectedCompanyTrialEndsAt');
    if (cachedStatus && cachedTrialEnd) {
      this.applyTrialStatus(cachedStatus, cachedTrialEnd);
    }

    // Also fetch fresh data from API (will update if trial ended meanwhile)
    this.http.get<any>(`${API_CONFIG.partnersService}/companies/${companyId}`).subscribe({
      next: (company) => {
        this.applyTrialStatus(company.subscriptionStatus, company.trialEndsAt);
      },
      error: (err) => {
        console.warn('[TrialBanner] Failed to fetch company data:', err?.status, err?.message);
      },
    });
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  private applyTrialStatus(subscriptionStatus: string | null, trialEndsAt: string | null) {
    if (subscriptionStatus === 'trialing' && trialEndsAt) {
      this.trialEndDate = new Date(trialEndsAt);
      this.updateCountdown();
      this.visible = true;
      if (!this.intervalId) {
        this.intervalId = setInterval(() => this.updateCountdown(), 60_000);
      }
    }
  }

  private updateCountdown() {
    if (!this.trialEndDate) return;

    const now = new Date();
    const diff = this.trialEndDate.getTime() - now.getTime();

    if (diff <= 0) {
      this.totalDays = 0;
      this.countdown = '00m : 00j : 00h : 00min';
      return;
    }

    const totalMinutes = Math.floor(diff / (1000 * 60));
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDaysRaw = Math.floor(totalHours / 24);
    this.totalDays = totalDaysRaw;

    const months = Math.floor(totalDaysRaw / 30);
    const days = totalDaysRaw % 30;
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;

    const pad = (n: number) => String(n).padStart(2, '0');
    this.countdown = `${pad(months)}m : ${pad(days)}j : ${pad(hours)}h : ${pad(minutes)}min`;
  }

  goToBilling() {
    this.router.navigate(['/billing']);
  }

}
